import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import { AppDispatch } from 'components/Common/Data/StoreCreator';
import { PAYWALL_PLANS_WITH_GROUPS_ENDPOINT_VERSION } from 'components/PayWall/api/PayWallApi';
import PayWallConfirmDialog from 'components/PayWall/PayWallConfirmDialog';
import PayWallDialog, { PAY_WALL_VERSION } from 'components/PayWall/PayWallDialog';
import {
  AVAILABLE_PLANS,
  closePayWall,
  fetchPlanData,
  REQUIRED_MOST_POPULAR,
  TOGGLE_PAYWALL_DIALOG_OPEN,
} from 'components/PayWall/Redux/Actions';
import { PayWallFullState, PayWallTiers } from 'components/PayWall/Redux/Reducer.type';
import { getAvailablePlansV3, getDialogStepFromUrl } from 'components/PayWall/Redux/Selectors';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import PayWallBillingAddressDialog from './PayWallBillingAddressDialog';
import PayWallContextProvider from './PayWallContextProvider';

type Props = {
  fromIframe?: boolean;
  planRequired?: string;
};

export default function PayWallDialogWrapper(props: Props): JSX.Element | null {
  const dispatch = useDispatch() as AppDispatch;
  const { trackEvent } = useTrackEventsService();

  const { fromIframe = false, planRequired = '' } = props;

  const paywallDataFetched = useSelector(getAvailablePlansV3);
  const dialogStepFromUrl = useSelector((state: PayWallFullState) => getDialogStepFromUrl(state));
  const dialogType = useSelector((state: PayWallFullState) => state.payWall.currentStep);
  const showDialog = useSelector((state: PayWallFullState) => state.payWall.showDialog);

  function updateCurrentSelectedPlan(requiredPlan: string) {
    dispatch({ type: TOGGLE_PAYWALL_DIALOG_OPEN, status: true, required: requiredPlan });
  }

  useEffect(() => {
    if (fromIframe) {
      if (isPresent(planRequired) && AVAILABLE_PLANS.includes(planRequired.toLowerCase() as PayWallTiers)) {
        updateCurrentSelectedPlan(planRequired.toLowerCase());
      } else {
        updateCurrentSelectedPlan(REQUIRED_MOST_POPULAR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => dispatch(closePayWall()), [dispatch]);

  function fetchData() {
    dispatch<any>(fetchPlanData(PAYWALL_PLANS_WITH_GROUPS_ENDPOINT_VERSION)).then(() => {
      trackEvent('Paywall viewed', { payWallVersion: PAY_WALL_VERSION });
      if (isBlank(dialogStepFromUrl)) {
        trackEvent(`Viewed choose plan Page`, {
          path: window.location.pathname,
          referrer: document.referrer,
          title: document.title,
          url: window.location.href,
          payWallVersion: PAY_WALL_VERSION,
        });
      }
    });
  }

  useEffect(() => {
    if (showDialog) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  if (paywallDataFetched) {
    return (
      <HousecallThemeProvider>
        <PayWallContextProvider>
          <PayWallDialog fromIframe={fromIframe} showDialog={showDialog} dialogType={dialogType} />
          <PayWallConfirmDialog />
          <PayWallBillingAddressDialog />
        </PayWallContextProvider>
      </HousecallThemeProvider>
    );
  }

  return null;
}
